import { ApiErrorMessages } from '@/components/element/ApiErrorMessages';
import { FormGroup } from '@/components/element/Form/FormGroup';
import { ScrollTop } from '@/components/element/ScrollTop';
import { WorkspaceLayout } from '@/components/layout/WorkspaceLayout';
import { usePaymentSchedules, usePaymentSchedulesInvalidate } from '@/hooks/api/0680_payment_schedules';
import { usePaymentTasksInvalidate } from '@/hooks/api/0960_payment_tasks';
import { useCreatePaymentTask } from '@/hooks/api/0970_create_payment_task';
import { PaymentSchedule, PaymentScheduleDetail } from '@/hooks/api/models';
import { toastApiError, toastSuccess } from '@/lib/toast';
import { useLoading } from '@/providers/LoadingProvider';
import { useNavState } from '@/providers/NavStateProvider';
import { useCurrentPurchaseOrder } from '@/providers/PurchaseOrderProvider';
import { useCurrentTeamMember } from '@/providers/TeamMemberProvider';
import { Japanese } from 'flatpickr/dist/l10n/ja';
import moment from 'moment/moment';
import { useEffect, useState } from 'react';
import Flatpickr from 'react-flatpickr';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';

export const PaymentTasksNew = () => {
  const { _w_, _t_, me } = useCurrentTeamMember();
  const { setTitle, setBreadcrumbs } = useNavState();
  const { _po_, mePurchaseOrderMember } = useCurrentPurchaseOrder();
  useEffect(() => setTitle(`新規支払タスク作成`), []);
  useEffect(
    () =>
      setBreadcrumbs([
        {
          label: `プロジェクト（費用） - ${_t_!.name}`,
          path: `/w/${_w_!.identifier}/teams/${_t_!.id}/purchase_orders`,
        },
        {
          label: `プロジェクト（費用）詳細`,
          path: `/w/${_w_!.identifier}/teams/${_t_!.id}/purchase_orders/${_po_!.id}`,
        },
      ]),
    []
  );

  const { data: schedules } = usePaymentSchedules({ identifier: _w_!.identifier, team_id: _t_!.id, id: _po_!.id });

  return (
    <WorkspaceLayout>
      <ScrollTop />

      {schedules && <_PaymentTaskNew schedules={schedules} />}
    </WorkspaceLayout>
  );
};

type FormInputs = {
  identifier: string;
  team_id: number;
  purchase_order_id: number;
  name: string;
  description?: string;
  payment_schedule_detail_id: number;
  due_date?: string;
};

const _PaymentTaskNew = ({ schedules }: { schedules: PaymentSchedule[] }) => {
  const navigate = useNavigate();
  const { setShowLoading } = useLoading();
  const { _w_, _t_, me } = useCurrentTeamMember();
  const { _po_, mePurchaseOrderMember } = useCurrentPurchaseOrder();
  const [scheduleDetails, setScheduleDetails] = useState<{ year_month: string; detail: PaymentScheduleDetail }[]>(
    () => {
      const res: { year_month: string; detail: PaymentScheduleDetail }[] = [];
      schedules.forEach((d) => {
        if (d.spot_detail && d.spot_closed == false) res.push({ year_month: d.year_month, detail: d.spot_detail });
        if (d.monthly_detail && d.monthly_closed == false)
          res.push({ year_month: d.year_month, detail: d.monthly_detail });
      });
      return res;
    }
  );
  const { mutate, data, isPending, error } = useCreatePaymentTask();
  const [autoSettingName, setAutoSettingName] = useState('');
  const { invalidate } = usePaymentTasksInvalidate(_w_!.identifier);
  const { invalidate: invalidatePaymentSchedules } = usePaymentSchedulesInvalidate({
    identifier: _w_!.identifier,
    team_id: _t_!.id,
    id: _po_!.id,
  });
  const {
    control,
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<FormInputs>({
    mode: 'onBlur',
    criteriaMode: 'all',
    defaultValues: {
      identifier: _w_!.identifier,
      team_id: _t_!.id,
      purchase_order_id: _po_!.id,
      payment_schedule_detail_id:
        schedules.length == 0 ? undefined : schedules[0].spot_detail?.id || schedules[0].monthly_detail?.id,
    },
  });

  const [paymentScheduleDetailId, name] = watch(['payment_schedule_detail_id', 'name']);

  useEffect(() => setShowLoading(isPending), [isPending]);
  useEffect(() => toastApiError(error), [error]);

  useEffect(() => {
    if (schedules) {
      const _schedules: { year_month: string; detail: PaymentScheduleDetail }[] = [];
      schedules.forEach((d) => {
        if (d.spot_detail && d.spot_closed == false)
          _schedules.push({ year_month: d.year_month, detail: d.spot_detail });
        if (d.monthly_detail && d.monthly_closed == false)
          _schedules.push({ year_month: d.year_month, detail: d.monthly_detail });
      });
      setScheduleDetails(_schedules);

      if (_schedules.length > 0) {
        setValue('payment_schedule_detail_id', _schedules[0].detail.id);
      }
    }
  }, [schedules]);

  // タスク名を自動設定する
  useEffect(() => {
    if (paymentScheduleDetailId) {
      const schedule = scheduleDetails.find((s) => s.detail.id == paymentScheduleDetailId);

      if (autoSettingName == '' || name == autoSettingName) {
        const _prefix = schedule
          ? `${schedule.year_month} ${schedule.detail.payment_context.name == 'spot' ? '一括' : '月額'}`
          : '';

        const _n = `${_prefix} 支払 - ${_po_!.name}`;
        setValue('name', _n);
        setAutoSettingName(_n);
      }

      if (schedule) {
        const ymd = moment(new Date(`${schedule?.year_month}/01`).toISOString())
          .add(1, 'M')
          .endOf('month')
          .format('YYYY/MM/DD');
        setValue('due_date', ymd);
      }
    }
  }, [paymentScheduleDetailId]);

  useEffect(() => {
    if (data) {
      invalidate();
      invalidatePaymentSchedules();
      toastSuccess('支払タスクの登録が完了しました');
      navigate(`/w/${_w_!.identifier}/teams/${_t_!.id}/purchase_orders/${_po_!.id}/payment_tasks/${data.id}`);
    }
  }, [data]);

  const onSubmit: SubmitHandler<FormInputs> = (data) => {
    setShowLoading(true);
    mutate(data);
  };

  return (
    <div className="container-fluid py-4">
      <div className="row">
        <div className="col-12 col-lg-9 mx-auto">
          <div className="card mb-4" id="basic-info">
            <div className="card-header d-flex align-items-center">
              <div className="col-md-8">
                <h5 className="mb-0">新規支払タスク作成</h5>
              </div>
            </div>
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="card-body pt-0 row">
                <ApiErrorMessages error={error} />

                <div className="form-group">
                  <label>プロジェクト名</label>
                  <p className="form-text text-muted text-sm ms-1">{_po_!.name}</p>
                </div>
                <FormGroup label="タスク名" required={true}>
                  <input
                    type="text"
                    maxLength={50}
                    className={`form-control ${errors.name && 'is-invalid'}`}
                    placeholder={'タスク名'}
                    {...register('name', {
                      required: '入力して下さい。',
                    })}
                  />
                  {errors.name && <div className="text-xs text-danger mt-1">{errors.name.message}</div>}
                </FormGroup>

                <FormGroup label="メモ">
                  <textarea className="form-control" maxLength={2000} rows={5} {...register('description')}></textarea>
                </FormGroup>

                <div className="col-12 col-md-6">
                  <FormGroup label="請求月" required={true}>
                    <select className="form-control" required={true} {...register('payment_schedule_detail_id')}>
                      {scheduleDetails.map((d) => (
                        <option key={`psd-${d.detail.id}`} value={d.detail.id}>
                          {d.year_month} {d.detail.payment_context.name == 'spot' ? '一括' : '月額'}
                        </option>
                      ))}
                    </select>
                  </FormGroup>
                </div>

                <div className="col-12 col-md-6">
                  <FormGroup label="期限日">
                    <Controller
                      control={control}
                      name="due_date"
                      render={({ field: { ref, ...field } }) => (
                        <Flatpickr
                          className={`form-control datepickr`}
                          {...field}
                          onChange={(_, dateString) => {
                            field.onChange(dateString);
                            field.onBlur();
                          }}
                          onBlur={() => {}}
                          options={{ locale: Japanese, dateFormat: 'Y/m/d' }}
                        />
                      )}
                    />
                  </FormGroup>
                </div>
              </div>
              <div className="card-footer d-flex align-items-center justify-content-end pt-0">
                <Link to="" className="btn btn-outline-secondary me-2" onClick={() => navigate(-1)}>
                  戻る
                </Link>
                <button className="btn bg-gradient-dark btn-icon">
                  <i className="fas fa-check-circle"></i>登録する
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};
